import { Component, OnInit, Input, OnDestroy, AfterViewInit, EventEmitter, Output } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { Schedule } from 'src/app/components/schedules/models/schedule';
import { ScheduleType } from 'src/app/components/schedules/models/schedule-type';
import moment from 'moment';
import { FormStatusEnum, ResourceType } from 'src/app/common/enumerations/enumerations';
import { ScheduleStatusEnum } from 'src/app/components/schedules/models/enums';
import { ScheduleData, ScheduleObject } from '../cl-editor/cl-editor-schedule/cl-editor-schedule.component';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { ScheduleResources } from 'src/app/components/schedules/models/schedule-resources';


@Component({
  selector: 'cl-schedule-and-checkbox',
  templateUrl: './cl-schedule-and-checkbox.component.html',
  styleUrls: ['./cl-schedule-and-checkbox.component.scss']
})
export class ClScheduleAndCheckboxComponent extends ClBaseComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() scheduleTypeId?: number;
  @Input() resourceID?: number | null;
  @Input() locationName?: string | null;

  @Output() scheduleChanged = new EventEmitter<ScheduleObject>();

  editing!: boolean;

  scheduleName?: string;

  scheduleType?: ScheduleType;
  scheduleTypes?: ScheduleType[];
  scheduleTypesFiltered?: ScheduleType[];
  scheduleTypes$!: Observable<ScheduleType[]>;
  scheduleTypesSubs!: Subscription;

  schedule?: Schedule;
  schedules?: Schedule[];
  schedulesFiltered?: Schedule[];
  schedules$!: Observable<Schedule[]>;
  schedulesSubs!: Subscription;

  resources?: Resource[];
  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;

  isActiveStatus?: boolean;
  matchedScheduleResource?: ScheduleResources;
  scheduleLocationName?: string | null;

  checkboxChanged(e: any) {
    this.checked = e.checked;
    this.answered.emit(this.checked);
  }

  override get labelHtml(): string {
    const before = this.stripDiv(this.stripStrong(this.textBefore ?? ''));
    const schedule = this.scheduleName ? ` <b class="blue">${this.scheduleName}</b> ` : '';
    const middle = this.stripDiv(this.stripStrong(this.textAfter ?? ''));
    const locationName = this.locationName?.trim() ? this.locationName : this.scheduleLocationName;
    const location = locationName ? ` <b class="blue">${locationName}</b> ` : '';
    const after2 = this.stripDiv(this.stripStrong(this.textAfter2 ?? ''));
    const flattened = this.flattenNestedB(`${before}${schedule}${middle}${location}${after2}`);
    return flattened.replace(/<b>/gi, '<b class="blue">');
  }

  ngOnInit() {

  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.isActiveStatus = this.checklist?.status != FormStatusEnum.Closed && this.checklist?.status != FormStatusEnum.Canceled;
      this.loadResources();
      this.loadScheduleTypes();
      this.loadSchedules();
    }, 100);
  }

  textChanged() {
    this.editing = false;
    this.changed.emit(this.textValue);
  }

  /**
   * Subscribe to schedule data, narrow it to the current schedule type, and refresh the selected schedule and warning state.
   * Also update the location name from the matched schedule when it has not already been provided.
   *
   * @remarks This mutates component state and retains the active subscription in `schedulesSubs`.
   */
  loadSchedules() {
    this.schedules$ = this.store.select(state => state.Schedules.data);
    this.schedulesSubs = this.schedules$.subscribe(data => {
      this.schedules = data?.filter(s => s.typeId == this.scheduleTypeId);
      this.refreshScheduleInfo();
    });
  }

  refreshScheduleInfo() {
    if (!this.schedules) return;

    // Keep only schedules for the configured type before resolving the active row.
    this.schedule = this.getScheduleBySR(this.scheduleResourceId, this.schedules);
    if (this.schedule) this.moreInfo = null;
    this.applyScheduleInfo(this.schedule);
    if (!this.schedule) this.applyFallbackLocationName();
    // Warn when the resolved expiration date is already in the past.
    const expireDate = this.schedule?.isExtended ? this.schedule.extendedDate : this.schedule?.expireDate;
    this.warning = moment(expireDate).isBefore(new Date);
  }

  /**
   * Frontend equivalent of KEBuilderService.ApplyScheduleInfo.
   * Uses the schedule resource id to populate schedule dates, schedule acronym,
   * and the display location name when those values were not already provided.
   */
  applyScheduleInfo(schedule?: Schedule) {
    if (!schedule) return;

    this.dateValue1 = schedule.lastCompleted;
    this.dateValue2 = schedule.nextDue;
    this.dateValue3 = schedule.extendedDate ?? null;
    this.scheduleType = this.scheduleType ?? this.scheduleTypes?.find(x => x.id == schedule.typeId) ?? schedule.type;
    this.scheduleName = this.scheduleName ?? this.scheduleType?.acronym;

    const scheduleResource = this.matchedScheduleResource ?? schedule.scheduleResources.find(r => r.id == this.scheduleResourceId);
    if (!scheduleResource) return;

    this.scheduleResourceId = scheduleResource.id ?? this.scheduleResourceId;
    this.resourceID = scheduleResource.resourceId ?? 0;

    if (!this.locationName?.trim()) {
      this.scheduleLocationName = this.resourceID != 0
        ? scheduleResource.resource?.name
        : scheduleResource.resourceString;
    }
  }

  /**
   * Resolve the schedule that owns the given schedule resource and, when needed, fall back to the non-completed schedule for the same resource.
   * Preserve the current selection when the active status workflow allows completed items to be replaced.
   *
   * @param scheduleResourceId Identify the schedule resource being displayed.
   * @param schedules Restrict the search to the schedules already filtered for the current type.
   * @returns The matching schedule, or `undefined` when no usable match exists.
   */
  getScheduleBySR(scheduleResourceId: number | undefined, schedules: Schedule[] | undefined): Schedule | undefined {
    this.matchedScheduleResource = undefined;
    const activeSchedules = this.sortActiveSchedules(schedules?.filter(schedule => this.isUncompletedSchedule(schedule)));
    const lookupResourceId = this.getScheduleLookupResourceId();

    const activeSchedule =
      this.findScheduleByResourceId(lookupResourceId, activeSchedules) ??
      this.findScheduleByResourceId(scheduleResourceId, activeSchedules) ??
      this.findScheduleByScheduleResourceId(scheduleResourceId, activeSchedules);

    if (activeSchedule) return activeSchedule;

    const completedSchedules = this.sortCompletedSchedules(
      schedules?.filter(schedule => !this.isUncompletedSchedule(schedule))
    );

    return this.findScheduleByResourceId(lookupResourceId, completedSchedules) ??
      this.findScheduleByResourceId(scheduleResourceId, completedSchedules) ??
      this.findScheduleByScheduleResourceId(scheduleResourceId, completedSchedules);
  }

  isUncompletedSchedule(schedule: Schedule): boolean {
    return schedule.statusId != ScheduleStatusEnum.completed;
  }

  findScheduleByScheduleResourceId(scheduleResourceId: number | null | undefined, schedules: Schedule[] | undefined): Schedule | undefined {
    if (scheduleResourceId == null) return undefined;
    const schedule = schedules?.find(x => x.scheduleResources.some(r => r.id == scheduleResourceId));
    this.matchedScheduleResource = schedule?.scheduleResources.find(r => r.id == scheduleResourceId);
    return schedule;
  }

  findScheduleByResourceId(resourceId: number | null | undefined, schedules: Schedule[] | undefined): Schedule | undefined {
    if (resourceId == null) return undefined;
    const schedule = schedules?.find(x => x.scheduleResources.some(r => r.resourceId == resourceId));
    this.matchedScheduleResource = schedule?.scheduleResources.find(r => r.resourceId == resourceId);
    return schedule;
  }

  sortCompletedSchedules(schedules: Schedule[] | undefined): Schedule[] | undefined {
    return schedules?.sort((a, b) => this.getCompletedSortDate(b) - this.getCompletedSortDate(a));
  }

  sortActiveSchedules(schedules: Schedule[] | undefined): Schedule[] | undefined {
    return schedules?.sort((a, b) => moment(b.nextDue).valueOf() - moment(a.nextDue).valueOf());
  }

  getCompletedSortDate(schedule: Schedule): number {
    return moment(schedule.completedDate ?? schedule.lastCompleted ?? schedule.nextDue).valueOf();
  }

  loadScheduleTypes() {
    this.scheduleTypes$ = this.store.select(state => state.ScheduleTypes.data);
    this.scheduleTypesSubs = this.scheduleTypes$.subscribe(data => {
      this.scheduleTypes = data;
      const scheduleType = data.find(x => x.id == this.scheduleTypeId);
      this.scheduleType = scheduleType;
      this.scheduleName = scheduleType?.acronym;
      this.refreshScheduleInfo();
    });
  }

  loadResources() {
    this.resources$ = this.store.select(state => state.Resources.data);
    this.resourcesSubs = this.resources$.subscribe(data => {
      this.resources = data;
      this.refreshScheduleInfo();
    });
  }

  getScheduleLookupResourceId(): number | null | undefined {
    const resource = this.resources?.find(r => r.id == this.resourceID);
    const scheduleTypeResourceTypes = this.getScheduleTypeResourceTypes();

    if (!resource || !scheduleTypeResourceTypes.length) return this.resourceID;

    if (scheduleTypeResourceTypes.includes(resource.type ?? 0) || scheduleTypeResourceTypes.includes(resource.resourceTypeID ?? 0)) {
      return resource.id;
    }

    if (scheduleTypeResourceTypes.includes(ResourceType.Endstation)) {
      return this.getRelatedResourceId(resource, ResourceType.Endstation)
        ?? this.getRelatedResourceId(this.getRelatedResource(resource, ResourceType.Beamline), ResourceType.Endstation)
        ?? this.resourceID;
    }

    if (scheduleTypeResourceTypes.includes(ResourceType.Beamline)) {
      return resource.childResources?.find(r => r.childResourceType == ResourceType.Beamline)?.childResourceID
        ?? resource.parentResources?.find(r => r.parentResourceType == ResourceType.Beamline)?.parentResourceID;
    }

    if (scheduleTypeResourceTypes.includes(ResourceType.Shutter)) {
      return resource.parentResources?.find(r => r.parentResourceType == ResourceType.Shutter)?.parentResourceID
        ?? resource.childResources?.find(r => r.childResourceType == ResourceType.Shutter)?.childResourceID;
    }

    return this.resourceID;
  }

  getRelatedResource(resource: Resource | null | undefined, resourceType: ResourceType): Resource | undefined {
    const resourceId = this.getRelatedResourceId(resource, resourceType);
    return this.resources?.find(r => r.id == resourceId);
  }

  getRelatedResourceId(resource: Resource | null | undefined, resourceType: ResourceType): number | undefined {
    return resource?.childResources?.find(r => r.childResourceType == resourceType)?.childResourceID
      ?? resource?.parentResources?.find(r => r.parentResourceType == resourceType)?.parentResourceID;
  }

  applyFallbackLocationName() {
    if (this.locationName?.trim()) return;

    const resourceId = this.getScheduleLookupResourceId();
    this.scheduleLocationName = this.resources?.find(r => r.id == resourceId)?.name;
  }

  getScheduleTypeResourceTypes(): number[] {
    const resourceTypeIds = this.scheduleType?.resourceTypeIds;
    const resourceTypes = resourceTypeIds ? this.utils.JSONparse(resourceTypeIds)?.map(Number) ?? [] : [];
    return resourceTypes;
  }

  saveScheduleResource(schedule: Schedule, resourceID: number) {
    const scheduleData = {
      id: schedule.scheduleResources.find(r => r.resourceId == resourceID)?.id ?? 0,
      resourceId: resourceID,
      name: schedule.scheduleResources.find(r => r.resourceId == resourceID)?.resource?.name ?? '',
      dateValue1: schedule.lastCompleted,
      dateValue2: schedule.nextDue,
      dateValue3: schedule.extendedDate
    } as ScheduleData;

    const scheduleObject = new ScheduleObject();
    scheduleObject.scheduleType = this.scheduleType;
    scheduleObject.scheduleData = scheduleData;
    this.scheduleChanged.emit(scheduleObject);
  }

  override ngOnDestroy() {
    this.scheduleTypesSubs?.unsubscribe();
    this.schedulesSubs?.unsubscribe();
    this.resourcesSubs?.unsubscribe();
  }
}
